import { Context } from "@nuxt/types";
import {
  Appointment,
  DocumentError,
  DocumentErrorCodes,
} from "@ambii-com/core";
import { allLanguages } from "~/utils/i18n";
import { normalizeTimestampsInData } from "~/utils/fsJs";
import { LocaleWithLangName } from "~/classes/LocaleWithLangName";
import { isFirestoreError } from "@/utils/typeChecks";
import {
  readForm,
  readFormsForClinic,
  readMenu,
  readMenusForClinic,
  readSchedule,
} from "~/services/firestoreReads";
import { getOpenApiHost } from "~/utils/openApiHost";

// for /appointment clinic specific pages, gets languages used in active appointment menus and sets available languages in header
// defaults to all languages supported in app
export default async function({ store, route, error }: Context) {
  if (!store.getters["auth/clinicId"] && route.path.includes("/appointment/")) {
    let clinicId: string;
    if (!!route.params.clinicId) {
      clinicId = route.params.clinicId;
    } else if (!!route.params.menuId) {
      clinicId = (await readMenu(route.params.menuId as string)).clinicId;
    } else if (!!route.params.scheduleId) {
      clinicId = (await readSchedule(route.params.scheduleId as string)).clinicId;
    } else if (!!route.params.appointmentId) {
      const res = await fetch(
        `${getOpenApiHost()}/oapi/appointments/${route.params.appointmentId}`
      );
      if (res.ok) {
        const { data } = await res.json();
        const normalizedData = normalizeTimestampsInData(data as Record<string, unknown>) as Record<string, unknown>;
        const docLike = {
          id: (data.id as string) || route.params.appointmentId,
          data: (opts?: unknown) => normalizedData,
          exists: true,
        };
        const appointment = Appointment.fromDoc(
          docLike as import("@firebase/firestore-types").DocumentSnapshot
        );
        clinicId = appointment.clinicId;
      }
    }
    if (clinicId) {
      const appointmentMenus = await readMenusForClinic(clinicId);
      
      // filter out submenus
      const parentMenus = appointmentMenus.filter(menu => !menu.isSubmenu);
      const filteredLanguages = allLanguages.filter(language =>
        parentMenus.some(menu => menu.languages.includes(language.locale))
      );
      const availableLanguages = filteredLanguages.length ? filteredLanguages : allLanguages;
      store.dispatch("appointment/setAvailableLanguages", availableLanguages);
    } else {
      store.dispatch("appointment/setAvailableLanguages", allLanguages);
    }
    // for /form clinic specific pages, gets available forms locale and sets available languages in header
  } else if (!store.getters["auth/clinicId"] && route.path.includes("/form/")) {
    let clinicId: string;
    if (!!route.params.clinicId) {
      clinicId = route.params.clinicId;
    } else if (!!route.params.formId) {
      try {
        const formData = await readForm(route.params.formId as string);
        clinicId = formData.clinicId;
      } catch (e) {
        if (e instanceof DocumentError) {
          switch (e.code) {
            case DocumentErrorCodes.DOC_NOT_FOUND || DocumentErrorCodes.DOC_MISSING_DATA:
              return error({
                statusCode: 404,
                custom: true,
                messageI18nKey: "error.message.404",
                titleI18nKey: "error.title.404"
              })
            default:
              return error({ statusCode: 500 })
          }
        } else if (isFirestoreError(e)) {
          switch (e.code) {
            case "unavailable":
              return error({
                statusCode: 400,
                custom: true,
                messageI18nKey: "error.message.unavailable400",
                titleI18nKey: "error.title.unavailable400",
              });
            case "deadline-exceeded":
              return error({ statusCode: 408 });
          }
        }
        return error({ statusCode: 500 });
      }
    }
    if (clinicId) {
      const clinicForms = await readFormsForClinic(clinicId);

      const availableForms = clinicForms.filter(form => form.available); // filter out forms that are not available
      const formLocales = new Set(availableForms.map(form => form.locale)); // set new object with locales of forms

      let availableLanguages: LocaleWithLangName[];
      if (!!clinicForms.length) {
        // filtering allLanguages with languages in formLocales
        availableLanguages = allLanguages.filter(language =>
          formLocales.has(language.locale)
        );
        store.dispatch("appointment/setAvailableLanguages", availableLanguages);
      }
    }
  }
}
